import { addMoney, compareMoney, multiplyMoney, scaleMoney, subtractMoney, zeroMoney } from "../money/arithmetic.js";
import { allocateMoneyByLineTotals } from "../money/allocate.js";
import { ceilDiv } from "../money/round.js";
import { venueLocalMoment } from "../time/venue-time.js";
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;
const BPS_SCALE = 10_000;
/** The units an Open session's time played is rounded up to (seconds): a minute to an hour. */
export const CONSOLE_BILLING_UNITS = [60, 300, 900, 1800, 3600];
/** The longest block, in seconds: a day. */
export const CONSOLE_MAX_BLOCK_SECONDS = 24 * SECONDS_PER_HOUR;
/** The most fixed block prices one class carries. */
export const CONSOLE_MAX_BLOCK_PRICES = 8;
/** The rules a class's controller pricing must keep (spec/06 §4.2). Empty: publishable. */
export function consoleRateProblems(rate, currency) {
    const problems = new Set();
    const amounts = [rate.firstControllerHourly, rate.extraControllerHourly, ...rate.blockPrices.map((block) => block.amount)];
    for (const amount of amounts) {
        if (amount.currency !== currency)
            problems.add('currency');
        if (!Number.isInteger(amount.amount) || amount.amount < 0)
            problems.add('negative-amount');
    }
    if (!CONSOLE_BILLING_UNITS.includes(rate.billingUnitSeconds))
        problems.add('billing-unit');
    if (rate.blockPrices.length > CONSOLE_MAX_BLOCK_PRICES)
        problems.add('too-many-blocks');
    const lengths = new Set();
    for (const block of rate.blockPrices) {
        if (!Number.isInteger(block.seconds) || block.seconds < SECONDS_PER_MINUTE || block.seconds > CONSOLE_MAX_BLOCK_SECONDS || block.seconds % SECONDS_PER_MINUTE !== 0) {
            problems.add('block-length');
        }
        if (lengths.has(block.seconds))
            problems.add('duplicate-block');
        lengths.add(block.seconds);
    }
    return [...problems];
}
/** The seconds an Open block charges for, having played `played`: rounded up to the billing unit (0 played is 0). */
export function openChargedSeconds(played, billingUnitSeconds) {
    const seconds = Math.max(0, Math.floor(played));
    if (seconds === 0)
        return 0;
    const unit = billingUnitSeconds > 0 ? billingUnitSeconds : SECONDS_PER_MINUTE;
    return ceilDiv(seconds, unit) * unit;
}
/** The controllers in force at `second` of the session's clock (at least 1). */
export function controllersAt(changes, second) {
    let current = changes[0]?.controllers ?? 1;
    for (const change of changes) {
        if (change.fromSecond <= second)
            current = change.controllers;
    }
    return Math.max(1, current);
}
/** The blocks laid end to end on the session's clock, an Open block sized by what was played. */
function spansOf(booking, rate) {
    const spans = [];
    let from = 0;
    booking.blocks.forEach((block, index) => {
        const seconds = block.seconds === null ? openChargedSeconds(booking.elapsedSeconds - from, rate.billingUnitSeconds) : Math.max(0, Math.floor(block.seconds));
        spans.push({ index, from, seconds, booked: block.seconds });
        from += seconds;
    });
    return spans;
}
/** A span cut at the controller changes inside it: each part with the controllers it had. */
function partsOf(span, changes) {
    const end = span.from + span.seconds;
    const cuts = [...new Set([span.from, ...changes.map((change) => change.fromSecond).filter((second) => second > span.from && second < end), end])].sort((a, b) => a - b);
    const parts = [];
    for (let i = 0; i + 1 < cuts.length; i += 1) {
        const from = cuts[i];
        const to = cuts[i + 1];
        if (to > from)
            parts.push({ from, seconds: to - from, controllers: controllersAt(changes, from) });
    }
    return parts;
}
/** The booking's lines at the rate and block prices, before offers (rules 1–3). */
function priceLines(rate, booking) {
    const spans = spansOf(booking, rate);
    const lines = [];
    for (const span of spans) {
        if (span.seconds <= 0)
            continue;
        const blockPrice = span.booked === null ? undefined : rate.blockPrices.find((block) => block.seconds === span.booked);
        const firstAmount = blockPrice === undefined ? scaleMoney(rate.firstControllerHourly, span.seconds, SECONDS_PER_HOUR) : blockPrice.amount;
        lines.push({
            kind: 'first_controller',
            blockIndex: span.index,
            blockSeconds: span.booked,
            fromSecond: span.from,
            seconds: span.seconds,
            quantity: 1,
            unitAmount: firstAmount,
            amount: firstAmount,
            basis: blockPrice === undefined ? 'rate' : 'block',
        });
        for (const part of partsOf(span, booking.controllers)) {
            const extras = part.controllers - 1;
            if (extras <= 0)
                continue;
            const unitAmount = scaleMoney(rate.extraControllerHourly, part.seconds, SECONDS_PER_HOUR);
            lines.push({
                kind: 'extra_controllers',
                blockIndex: span.index,
                blockSeconds: span.booked,
                fromSecond: part.from,
                seconds: part.seconds,
                quantity: extras,
                unitAmount,
                amount: multiplyMoney(unitAmount, extras),
                basis: 'rate',
            });
        }
    }
    return { lines, spans };
}
const DAY_NUMBER = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
/** Whether an offer is valid, at this venue, on this local day and inside its local window at `context.at`. */
function offerOpen(offer, context) {
    if (!offer.active)
        return false;
    if (offer.validFrom !== null && context.at < offer.validFrom)
        return false;
    if (offer.validUntil !== null && context.at > offer.validUntil)
        return false;
    if (offer.venueIds.length > 0 && !offer.venueIds.includes(context.venueId))
        return false;
    if (offer.stationClassIds.length > 0 && (context.stationClassId === null || !offer.stationClassIds.includes(context.stationClassId)))
        return false;
    if (offer.playersOnly && !context.hasPlayer)
        return false;
    if (offer.daysOfWeek.length === 0 && (offer.startMinuteOfDay === null || offer.endMinuteOfDay === null))
        return true;
    const local = venueLocalMoment(context.at, context.timezone);
    if (offer.daysOfWeek.length > 0 && !offer.daysOfWeek.includes(DAY_NUMBER[local.weekday] ?? -1))
        return false;
    if (offer.startMinuteOfDay !== null && offer.endMinuteOfDay !== null) {
        const minute = Math.floor(local.secondOfDay / SECONDS_PER_MINUTE);
        if (minute < offer.startMinuteOfDay || minute > offer.endMinuteOfDay)
            return false;
    }
    return true;
}
const clip = (value, ceiling) => (compareMoney(value, ceiling) > 0 ? ceiling : value);
const floorZero = (value) => (value.amount < 0 ? zeroMoney(value.currency) : value);
const sumOf = (values, currency) => values.reduce((total, value) => addMoney(total, value), zeroMoney(currency));
/** What one offer would take off, before stacking, and which lines it targets. Zero when it does not apply. */
function offerDiscount(offer, rate, lines, facts, currency) {
    const zero = zeroMoney(currency);
    const none = { amount: zero, targets: [] };
    if (offer.minControllers !== null && facts.peak < offer.minControllers)
        return none;
    if (offer.minDurationSeconds !== null && facts.chargedSeconds < offer.minDurationSeconds)
        return none;
    const all = lines.map((_, index) => index);
    const extras = all.filter((index) => lines[index].kind === 'extra_controllers');
    const subtotalOf = (targets) => sumOf(targets.map((index) => lines[index].amount), currency);
    const reward = offer.reward;
    let amount;
    let targets;
    switch (reward.kind) {
        case 'extra_controllers_percent':
            targets = extras;
            amount = scaleMoney(subtotalOf(extras), Math.min(BPS_SCALE, Math.max(0, reward.percentBps)), BPS_SCALE);
            break;
        case 'extra_controllers_amount':
            if (reward.amount.currency !== currency)
                return none;
            targets = extras;
            amount = multiplyMoney(reward.amount, Math.max(0, facts.peak - 1));
            break;
        case 'session_percent':
            targets = all;
            amount = scaleMoney(subtotalOf(all), Math.min(BPS_SCALE, Math.max(0, reward.percentBps)), BPS_SCALE);
            break;
        case 'bundle': {
            if (reward.amount.currency !== currency)
                return none;
            if (facts.peak < reward.controllers || facts.chargedSeconds < reward.seconds)
                return none;
            // What `controllers` controllers for `seconds` cost on this class, as one booked block: the bundle's own price
            // replaces that.
            const normal = priceLines(rate, { blocks: [{ seconds: reward.seconds }], controllers: [{ fromSecond: 0, controllers: reward.controllers }], elapsedSeconds: 0 });
            targets = all;
            amount = floorZero(subtractMoney(sumOf(normal.lines.map((line) => line.amount), currency), reward.amount));
            break;
        }
        case 'free_time': {
            if (facts.chargedSeconds < reward.paidSeconds + reward.freeSeconds)
                return none;
            // The free stretch at the rates, for the controllers the session peaked at.
            targets = all;
            amount = addMoney(scaleMoney(rate.firstControllerHourly, reward.freeSeconds, SECONDS_PER_HOUR), multiplyMoney(scaleMoney(rate.extraControllerHourly, reward.freeSeconds, SECONDS_PER_HOUR), Math.max(0, facts.peak - 1)));
            break;
        }
    }
    if (targets.length === 0)
        return none;
    if (offer.maxDiscount !== null && offer.maxDiscount.currency === currency)
        amount = clip(amount, offer.maxDiscount);
    amount = clip(floorZero(amount), subtotalOf(targets));
    return amount.amount > 0 ? { amount, targets } : none;
}
/**
 * The price of a console session (rules 1–4): its lines, the offers taken off them, and the total. The one function
 * the start preview, the add-time preview, the running session's "Due at the end" and the final order all call.
 */
export function quoteConsoleSession(rate, booking, offers, context) {
    const currency = rate.firstControllerHourly.currency;
    const zero = zeroMoney(currency);
    const { lines, spans } = priceLines(rate, booking);
    const chargedSeconds = spans.reduce((total, span) => total + span.seconds, 0);
    const end = chargedSeconds;
    const peak = Math.max(1, ...booking.controllers.filter((change) => change.fromSecond === 0 || change.fromSecond < Math.max(end, booking.elapsedSeconds)).map((change) => change.controllers));
    const open = booking.blocks.length > 0 && booking.blocks[booking.blocks.length - 1].seconds === null;
    const nowControllers = controllersAt(booking.controllers, Math.max(0, open ? booking.elapsedSeconds : end - 1));
    const hourlyAmount = addMoney(rate.firstControllerHourly, multiplyMoney(rate.extraControllerHourly, nowControllers - 1));
    const subtotal = sumOf(lines.map((line) => line.amount), currency);
    // Rule 4: the candidates, then stacking.
    const candidates = offers
        .filter((offer) => offerOpen(offer, context))
        .map((offer) => ({ offer, ...offerDiscount(offer, rate, lines, { peak, chargedSeconds }, currency) }))
        .filter((candidate) => candidate.amount.amount > 0);
    let chosen = candidates;
    if (candidates.length > 0 && !candidates.every((candidate) => candidate.offer.stackable)) {
        chosen = [
            candidates.reduce((best, candidate) => {
                const better = compareMoney(candidate.amount, best.amount);
                if (better > 0)
                    return candidate;
                if (better === 0 && candidate.offer.priority > best.offer.priority)
                    return candidate;
                return best;
            }),
        ];
    }
    chosen = [...chosen].sort((a, b) => b.offer.priority - a.offer.priority);
    // Each chosen offer is clipped to what its lines have left, then split across them by what they have left.
    const left = lines.map((line) => line.amount);
    const discounts = lines.map(() => zero);
    const applied = [];
    for (const candidate of chosen) {
        const room = sumOf(candidate.targets.map((index) => left[index]), currency);
        const amount = clip(candidate.amount, room);
        if (amount.amount <= 0)
            continue;
        const pieces = allocateMoneyByLineTotals(amount, candidate.targets.map((index) => left[index]));
        candidate.targets.forEach((lineIndex, position) => {
            const piece = clip(pieces[position], left[lineIndex]);
            discounts[lineIndex] = addMoney(discounts[lineIndex], piece);
            left[lineIndex] = subtractMoney(left[lineIndex], piece);
        });
        applied.push({ offerId: candidate.offer.id, name: candidate.offer.name, amount });
    }
    const discount = sumOf(discounts, currency);
    return {
        lines: lines.map((line, index) => ({ ...line, discount: discounts[index] })),
        subtotal,
        discount,
        total: floorZero(subtractMoney(subtotal, discount)),
        applied,
        chargedSeconds,
        open,
        peakControllers: peak,
        hourlyAmount,
    };
}
/** The booked seconds of a booking's blocks; null when the last block is Open (no end). */
export function bookedSecondsOf(blocks) {
    let total = 0;
    for (const block of blocks) {
        if (block.seconds === null)
            return null;
        total += block.seconds;
    }
    return total;
}
//# sourceMappingURL=console-pricing.js.map